import { useState } from 'react';

const isValidDecimal = (value: string) => /^\d*(\.\d{0,2})?$/.test(value);
const normalizeLeadingDot = (value: string) => (value.startsWith('.') ? `0${value}` : value);
const formatValue = (value: number | null) => (value !== null && value !== undefined ? Number(value).toFixed(2) : '');

interface UseDecimalFieldOptions {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
}

/**
 * Backs a controlled text input representing a bounded decimal number (currency
 * amount, percentage, etc). While not focused, the displayed value is derived
 * straight from `value`. While focused, a local draft owns the text so partial
 * states (e.g. "5.", ".2") survive keystrokes without the formatted value
 * clobbering them - see FeeInput's history for why this shape is needed.
 */
export const useDecimalField = ({ value, onChange, min = 0, max = Infinity }: UseDecimalFieldOptions) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const displayedValue = isEditing ? draft : formatValue(value);

  const handleFocus = () => {
    setDraft(formatValue(value));
    setIsEditing(true);
  };

  const handleChange = (rawValue: string) => {
    const normalized = normalizeLeadingDot(rawValue);
    if (!isValidDecimal(normalized)) return;
    const num = normalized ? Number(normalized) : null;
    if (num !== null && (num < min || num > max)) return;
    setDraft(normalized);
    onChange(num);
  };

  const handleBlur = () => {
    if (draft) {
      onChange(Number(Number(draft).toFixed(2)));
    }
    setIsEditing(false);
  };

  return { displayedValue, handleFocus, handleChange, handleBlur };
};
